<script>
  import { createEventDispatcher } from "svelte";
  import Modal from "@components/Modal.svelte";
  import Button from "@components/Button.svelte";
  import DeleteIcon from "@components/common/icons/app/DeleteIcon.svelte";
  import TableEditIcon from "@components/common/icons/app/TableEditIcon.svelte";
  import PlusIcon from "@components/common/icons/app/PlusIcon.svelte";
  import WarningFilledIcon from "@components/common/icons/app/WarningFilledIcon.svelte";
  import ProcessIcon from "@components/common/icons/app/ProcessIcon.svelte";
  import ListIcon from "@components/common/icons/app/ListIcon.svelte";
  import CheckboxIcon from "@components/common/icons/app/CheckboxIcon.svelte";

  export let open = false;
  export let heading = "Import Framework";
  export let replaceFramework = false;
  export let impactSummary = {
    processes: 0,
    subprocesses: 0,
    tasks: 0,
    subtasks: 0,
  };

  const dispatch = createEventDispatcher();
  let extendFramework = true;
  let currentStep = 1;

  $: replaceFramework = !extendFramework;

  function handleSecondaryClick() {
    if (currentStep === 2) {
      handleBack();
    } else {
      open = false;
      dispatch("close");
      currentStep = 1;
    }
  }

  function handleClose() {
    dispatch("close");
    currentStep = 1;
    extendFramework = true;
  }

  async function handlePrimaryAction() {
    console.log({ currentStep, extendFramework, replaceFramework });

    if (currentStep === 1 && !extendFramework) {
      currentStep = 2;
    } else {
      dispatch("continue", { replaceFramework });
      open = false;
    }
  }

  function handleBack() {
    if (currentStep > 1) {
      currentStep -= 1;
    }
  }
</script>

<Modal
  bind:open
  size="sm"
  noSecondaryButton
  actions
  autowidth
  preventCloseOnClickOutside
  modalHeading={heading}
  primaryButtonText={currentStep === 1 ? "Continue" : "Replace Framework"}
  primaryButtonDanger={currentStep === 2}
  primaryButtonAction={handlePrimaryAction}
  on:close={handleClose}
>
  <div class="import-framework-modal">
    {#if currentStep === 1}
      <div class="copy-block center">
        <div class="icon-wrapper info-icon">
          <TableEditIcon color="#e86a33" />
        </div>
        <h3>Existing framework detected</h3>
        <p>A framework already exists for this import source.</p>
        <p class="question">How would you like to continue?</p>
      </div>

      <div class="options-container">
        <label class="option-card" class:selected={extendFramework}>
          <input
            type="radio"
            bind:group={extendFramework}
            value={true}
            name="framework-action"
          />
          <div class="radio-circle"></div>
          <div class="option-icon extend-icon">
            <PlusIcon color="#198038" size="36" />
          </div>
          <div class="option-text">
            <h4>Extend existing framework</h4>
            <p>Keeps current mappings and adds new fields from this import.</p>
          </div>
        </label>

        <label class="option-card" class:selected={!extendFramework}>
          <input
            type="radio"
            bind:group={extendFramework}
            value={false}
            name="framework-action"
          />
          <div class="radio-circle"></div>
          <div class="option-icon replace-icon">
            <DeleteIcon color="#da1e28" />
          </div>
          <div class="option-text">
            <h4>Replace existing framework</h4>
            <p>
              Deletes the current framework and creates a new one from the
              uploaded file.
            </p>
          </div>
        </label>
      </div>
    {:else if currentStep === 2}
      <div class="copy-block center">
        <div class="icon-wrapper alert-icon">
          <WarningFilledIcon color="#da1e28" size="36" />
        </div>
        <h3>Replace framework?</h3>
        <p>
          The existing framework and all mappings will be permanently removed.
        </p>
        <p class="danger-text">This action cannot be undone.</p>
      </div>

      <div class="impact-panel">
        <h5>This will remove:</h5>
        <ul>
          <li>
            <ProcessIcon color="#057a28" size="20" />
            <span class="count">{impactSummary.processes}</span> Processes (L1)
          </li>
          <li>
            <ProcessIcon color="#057a28" size="20" />
            <span class="count">{impactSummary.subprocesses}</span> Subprocesses
            (L2)
          </li>
          <li>
            <ListIcon color="#057a28" size="22" />
            <span class="count">{impactSummary.tasks}</span> Tasks (L3)
          </li>
          <li>
            <CheckboxIcon color="#057a28" size="20" />
            <span class="count">{impactSummary.subtasks}</span> Subtasks (L4)
          </li>
        </ul>
      </div>
    {/if}
  </div>
  <div slot="left-buttons">
    {#if currentStep === 1}
      <Button
        text={currentStep > 1 ? "Back" : "Cancel"}
        on:click={handleBack}
        variant="outline"
        iconLeft={currentStep > 1}
        icon={currentStep > 1 ? "arrow_left_dark" : null}
      />
    {/if}
  </div>
</Modal>

<style>
  .import-framework-modal {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
    min-width: 32rem;
    padding: 1rem 0.5rem;
    font-family: Inter, sans-serif;
  }

  .center {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .icon-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
  }
  .info-icon {
    background-color: #fff0ea;
    color: #e86a33;
  }
  .alert-icon {
    background-color: #ffebeb;
    color: #da1e28;
  }

  .copy-block h3 {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
    font-weight: 700;
    color: #161616;
  }

  .copy-block p {
    margin: 0 0 0.25rem;
    font-size: 0.875rem;
    color: #6f6f6f;
    padding-right: 0 !important;
  }

  .copy-block .question {
    margin-top: 0.75rem;
    font-weight: 500;
    color: #161616;
  }

  .danger-text {
    color: #da1e28;
    font-weight: 600;
    font-size: 0.875rem;
    margin-top: 0.5rem !important;
  }

  .options-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .option-card {
    display: flex;
    align-items: center;
    padding: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }

  .option-card input[type="radio"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  /* Custom Radio Circle */
  .radio-circle {
    width: 18px;
    height: 18px;
    border: 2px solid #a8a8a8;
    border-radius: 50%;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .option-card input[type="radio"]:checked + .radio-circle {
    border-color: #111111;
  }

  .option-card input[type="radio"]:checked + .radio-circle::after {
    content: "";
    width: 10px;
    height: 10px;
    background-color: #111111;
    border-radius: 50%;
  }

  .selected {
    border-color: #e0e0e0;
    background-color: #fcfcfc;
  }

  .option-card:hover {
    border-color: #a8a8a8;
  }

  .option-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .extend-icon {
    background-color: #e3f9e5;
    color: #198038;
  }
  .replace-icon {
    background-color: #ffebeb;
    color: #da1e28;
  }

  .option-text h4 {
    margin: 0 0 0.15rem;
    font-size: 0.9375rem;
    font-weight: 600;
    color: #161616;
  }

  .option-text p {
    margin: 0;
    font-size: 0.8125rem;
    color: #6f6f6f;
    line-height: 1.3;
  }

  /* Panel de Impacto (Step 2) */
  .impact-panel {
    background-color: #fff5f5;
    border: 1px solid #ffd0d0;
    border-radius: 8px;
    padding: 1.25rem;
  }

  .impact-panel h5 {
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #161616;
  }

  .impact-panel ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .impact-panel li {
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: #4d4d4d;
  }

  .count {
    font-weight: 600;
    color: #161616;
    margin-right: 0.25rem;
    margin-left: 0.75rem;
  }
</style>
